import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import axios from 'axios';

const KubernetesClusterDetailsPage = () => {
    const { clusterId } = useParams();
    const { selectedAKSCluster } = useSelector((state: RootState) => state.monitoring);

    const [cluster, setCluster] = useState<any>(null);
    const [apps, setApps] = useState<any[]>([]);
    const [services, setServices] = useState<any[]>([]);
    const [daprComponents, setDaprComponents] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<any>('apps');

    useEffect(() => {
        const fetchClusterData = async () => {
            try {
                setLoading(true);

                // Fetch cluster details
                const clusterResponse = await axios.get(`/api/monitoring/clusters`);
                const clusterData = clusterResponse.data.data.find((c: any) => c.name === clusterId);
                setCluster(clusterData);

                // Check if current cluster matches selected AKS cluster filter
                if (selectedAKSCluster && clusterData.name !== selectedAKSCluster.name) {
                    // Cluster doesn't match filter
                    setApps([]);
                    setServices([]);
                    setDaprComponents([]);
                } else {
                    // Fetch cluster applications
                    const appsResponse = await axios.get(`/api/monitoring/clusters/${clusterId}/apps`);
                    setApps(appsResponse.data.data);

                    // Fetch cluster services
                    const servicesResponse = await axios.get(`/api/monitoring/clusters/${clusterId}/services`);
                    setServices(servicesResponse.data.data);

                    // Fetch cluster Dapr components
                    const daprResponse = await axios.get(`/api/monitoring/clusters/${clusterId}/dapr-components`);
                    setDaprComponents(daprResponse.data.data);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching cluster data:', error);
                setLoading(false);
            }
        };

        fetchClusterData();
    }, [clusterId, selectedAKSCluster]);

    const viewLogs = (resourceType: any, resource: any) => {
        let url = `/kubernetes/logs?cluster=${clusterId}&namespace=${resource.namespace}&pod=${resource.name}`;
        window.open(url, '_blank');
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading cluster details...</span>
                </div>
            </div>
        );
    }

    if (!cluster) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <p className="text-yellow-800 dark:text-yellow-200">Cluster not found</p>
                </div>
            </div>
        );
    }

    // Check if cluster is filtered out
    const isFilteredOut = selectedAKSCluster && cluster.name !== selectedAKSCluster.name;

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex items-center mb-6">
                <Link
                    to="/kubernetes/clusters"
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                >
                    ? Back to Clusters
                </Link>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{cluster.name}</h1>
                <span className={`ml-4 px-3 py-1 text-sm font-semibold rounded-full ${cluster.healthStatus === 'Healthy'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                    {cluster.healthStatus}
                </span>
            </div>

            {isFilteredOut && (
                <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-blue-800 dark:text-blue-200">
                        This cluster is not part of the selected filter. Select "All Clusters" or "{cluster.name}" from the sidebar to view its details.
                    </p>
                </div>
            )}

            {/* Cluster Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Nodes</div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                        {cluster.readyNodes}/{cluster.nodeCount}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">CPU Usage</div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{cluster.cpuUsage}%</div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mt-2">
                        <div
                            className={`h-2.5 rounded-full ${cluster.cpuUsage > 80 ? 'bg-red-500' :
                                    cluster.cpuUsage > 60 ? 'bg-yellow-500' :
                                        'bg-green-500'
                                }`}
                            style={{ width: `${cluster.cpuUsage}%` }}
                        ></div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Memory Usage</div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{cluster.memoryUsage}%</div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mt-2">
                        <div
                            className={`h-2.5 rounded-full ${cluster.memoryUsage > 80 ? 'bg-red-500' :
                                    cluster.memoryUsage > 60 ? 'bg-yellow-500' :
                                        'bg-blue-500'
                                }`}
                            style={{ width: `${cluster.memoryUsage}%` }}
                        ></div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Pods</div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                        {cluster.readyPods}/{cluster.totalPods}
                    </div>
                </div>
            </div>

            {!isFilteredOut && (
                <>
                    {/* Tabs */}
                    <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                        <nav className="-mb-px flex">
                            <button
                                className={`py-2 px-4 border-b-2 ${activeTab === 'apps'
                                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                onClick={() => setActiveTab('apps')}
                            >
                                Applications ({apps.length})
                            </button>
                            <button
                                className={`ml-8 py-2 px-4 border-b-2 ${activeTab === 'services'
                                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                onClick={() => setActiveTab('services')}
                            >
                                Services ({services.length})
                            </button>
                            <button
                                className={`ml-8 py-2 px-4 border-b-2 ${activeTab === 'dapr'
                                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                onClick={() => setActiveTab('dapr')}
                            >
                                Dapr Components ({daprComponents.length})
                            </button>
                        </nav>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'apps' && (
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Replicas</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Dapr</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {apps.map((app: any, index: number) => (
                                        <tr
                                            key={`${app.namespace}-${app.name}-${index}`}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                            onClick={() => window.location.href = `/kubernetes/apps/${clusterId}/${app.namespace}/${app.name}`}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Link
                                                    to={`/kubernetes/apps/${clusterId}/${app.namespace}/${app.name}`}
                                                    onClick={e => e.stopPropagation()}
                                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                                >
                                                    {app.name}
                                                </Link>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{app.namespace}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{app.type}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${app.status === 'Available'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : app.status === 'Progressing'
                                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                    }`}>
                                                    {app.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{app.replicas}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {app.hasDapr ? (
                                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                        {app.daprAppId || 'Enabled'}
                                                    </span>
                                                ) : '�'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        viewLogs('app', app);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-2"
                                                >
                                                    View Logs
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {apps.length === 0 && (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    No applications found in this cluster
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'services' && (
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cluster IP</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ports</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {services.map((service: any, index: number) => (
                                        <tr key={`${service.namespace}-${service.name}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{service.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{service.namespace}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${service.type === 'LoadBalancer'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : service.type === 'NodePort'
                                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                                    }`}>
                                                    {service.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{service.clusterIP || '�'}</td>
                                            <td className="px-6 py-4 text-gray-900 dark:text-white">
                                                {service.ports && service.ports.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {service.ports.map((port: any, idx: number) => (
                                                            <div key={idx} className="text-sm">
                                                                {port.port}{port.targetPort ? `:${port.targetPort}` : ''}/{port.protocol}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : '�'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {services.length === 0 && (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    No services found in this cluster
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'dapr' && (
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {daprComponents.map((component: any, index: number) => (
                                        <tr key={`${component.namespace}-${component.name}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{component.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{component.namespace}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{component.type}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${component.status === 'Healthy'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                    }`}>
                                                    {component.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {daprComponents.length === 0 && (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    No Dapr components found in this cluster
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default KubernetesClusterDetailsPage;